import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

// Import logo
import { LOGO_COLLAPSED_DATA } from '../../assets/logoInline';

// Navigation Component with Sticky Header
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { label: 'Features', href: '/features' },
    { label: 'Use Cases', href: '/use-cases' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/80 backdrop-blur-xl border-b border-white/5 shadow-lg shadow-black/20' 
          : 'bg-transparent'
      }`}
      data-testid="sticky-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <nav className="flex items-center justify-between h-16 sm:h-18 md:h-20" data-testid="marketing-nav">
          {/* Logo - Navbar: 32-36px height */}
          <Link to="/" className="flex items-center gap-3" data-testid="marketing-logo">
            <img 
              src={LOGO_COLLAPSED_DATA} 
              alt="MissionAssist" 
              className="h-[34px] w-auto rounded-lg"
            />
            <span className="text-lg sm:text-xl font-semibold text-white">MissionAssist</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA - Prominent Book Demo */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
              data-testid="nav-login-link"
            >
              Log In
            </Link>
            <Link
              to="/demo"
              className="inline-flex items-center justify-center gap-2 h-10 px-6 rounded-full bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-400 transition-all shadow-lg shadow-indigo-500/25"
              data-testid="nav-demo-button"
            >
              Book Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-4 sm:px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block py-2 text-base font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <hr className="border-white/10" />
            <Link
              to="/login"
              className="block py-2 text-base font-medium text-slate-400 hover:text-white transition-colors"
            >
              Log In
            </Link>
            <Link
              to="/demo"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-400 transition-colors"
            >
              Book Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

// Footer Component with Logo
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-8">
          {/* Brand with Logo - Section Logo: 32-40px */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-4 mb-4" data-testid="footer-logo">
              <img 
                src={LOGO_COLLAPSED_DATA} 
                alt="MissionAssist" 
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg"
              />
              <span className="text-xl font-semibold text-white">MissionAssist</span>
            </Link>
            <p className="text-slate-400 text-sm max-w-xs leading-relaxed">
              The Operating System for Insurance Agencies. Connect funnels, leads, bookings, compliance, and workflows in one platform.
            </p>
          </div>

          {/* Platform */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Platform</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/features" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Features
                </Link>
              </li>
              <li>
                <Link to="/use-cases" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Use Cases
                </Link>
              </li>
              <li>
                <Link to="/demo" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Book Demo
                </Link>
              </li>
            </ul>
          </div>

          {/* Account */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Account</h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/login" 
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Log In
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:support@missionassist.ai" 
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Contact Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar - Removed "Powered by ORBIT" */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {currentYear} MissionAssist. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Marketing Layout
export default function MarketingLayout() {
  // PostHog Analytics - Only for Public Marketing Pages
  // Tracks anonymous visitor behavior on landing pages (heat maps, conversions)
  // Does NOT track authenticated MissionAssist users
  useEffect(() => {
    // Load PostHog script dynamically
    const script = document.createElement('script');
    script.innerHTML = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init("phc_yHzTTwYMUQkr2NEKYqtRw394w8PJpV7A5CpKotGQKfVG",{api_host:"https://us.i.posthog.com",person_profiles:"always",session_recording:{recordCrossOriginIframes:true}});
    `;
    document.head.appendChild(script);

    return () => {
      // Cleanup PostHog when leaving marketing pages
      if (window.posthog) {
        try {
          window.posthog.opt_out_capturing();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-app flex flex-col" data-testid="marketing-layout">
      <Navigation />
      <div className="flex-1">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}
